import { Messages } from "../../db";
import { upsertConversationMeta } from "../../db/ConversationMetadata.js";
import useConversationStore from "../../stores/ConversationStore";
import { loadLocalKeys } from "../../db/KeyStorageManager";
import { storeHandshakeRequest } from "../../db/HandshakeRequests";
import { getSDK as getUnlinkSDK } from "../../sdk/UnlinkInstance";
import { createMessageObject } from "../../types/Message";
import bs58 from "bs58";
import { upsertRatchetKeys } from '../../db/RatchetedConversationKeys';

/**
 * Handle and route a parsed chat message.
 * @param {Object} message
 * @param {string} message.messageId
 * @param {string} message.conversationId
 * @param {Object} message.parsed
 * @param {number} message.timestamp
 * @param {string} message.viewKey - public view key
 * @param {string} message.alias
 * @param {string} message.password - user's password (must be provided to decrypt later)
 */
export default async function dispatchParsedHandshakeConfirm({
  message, 
  receiverIdentityViewPublicKey,
  localIsSender, 
  userId, 
  password, 
  salt,
}) {
  const unlinkSdk = getUnlinkSDK();
  const unlinkKeys = await loadLocalKeys({ userId, password, salt });

  const { parsed, senderViewPublicKey, conversationId, messageType, kyberKeys } = message;
  
  if (!parsed || !senderViewPublicKey || !conversationId || !messageType || !kyberKeys) {
    throw new Error("[dispatchParsedHandshakeConfirm] message incomplete", message);
  }

  const timestamp = Date.now();

  if (!localIsSender) {
    await upsertRatchetKeys({
      conversationId,
      contactIdentityViewPublicKey: senderViewPublicKey,
      sessionNextKyberPublicKey: kyberKeys.kyberPublicKey,
      sessionNextKyberPrivateKey: kyberKeys.kyberPrivateKey,
      sessionNextKyberSecret: kyberKeys.kyberSecret,
      userId,
      password,
    });
  }

  // create message object
  const messageJSON = JSON.stringify(createMessageObject({
    message: {
      kyberCiphertext: bs58.encode(parsed.kyberCiphertext),
      version: parsed.version,
    },
    messageId: message.messageId,
    messageType,
    receiverViewPublicKey: bs58.encode(receiverIdentityViewPublicKey),
    senderViewPublicKey: bs58.encode(senderViewPublicKey),
    totalFeeLamports: message.totalFeeLamports,
    timestamp,
  }));

  // Save the full message encrypted
  await Messages.storeEncryptedMessage({
    conversationId,
    messageJSON,
    timestamp,
    userId,
    password,
  });

  // Upsert into metadata table (conversation store)
  await upsertConversationMeta({
    conversationId,
    conversationName: null,
    lastMessage: messageJSON,
    participants: [senderViewPublicKey, receiverIdentityViewPublicKey],
    senderViewPublicKey: senderViewPublicKey,
    userId,
    password,
  });
}